import { useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import Logo from 'fe-design-base/assets/Logo';
import { useIsBrowserOnMobile } from 'hooks/useIsBrowserOnMobile';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { dimensions } from 'theme/constants';

import * as navActions from 'actions/nav';
import { toggleNotificationsShown } from 'actions/notifications';

import * as sessionSelectors from 'selectors/session';

import BillingStateBanner from 'features/biller/billingStateBanner/BillingStateBanner';
import EnforcementBanner from 'features/enforcement/EnforcementBanner/EnforcementBanner';
import { enrollEntityInExperiment } from 'features/experiments/actions';
import { getViewLogic } from 'features/experiments/selectors';
import PayrollPreNoteValidationBanner from 'features/nav/HeaderNavBanners/PayrollPreNoteValidationBanner';
import { FULL_SCREEN_PATHS } from 'features/payroll/constants';

import AnnounceKitButton from 'components/AnnounceKit/AnnounceKitButton';
import Box from 'components/Box';

import { cxHelpers } from 'util/className';
import { datadogLogInfo } from 'util/datadogLogs';
import { EVENT_CATEGORIES, PRODUCT_AREAS } from 'util/tracking_constants';
import { withUxRoot } from 'util/uxEvents';

import GlobalComponents from '../GlobalComponents';
import ActiveTrialNavItem from '../items/ActiveTrialNavItem';
import AvatarMenuNavItem from '../items/AvatarMenuNavItem';
import LocationsMenuNavItem from '../items/LocationsMenuNavItem';
import MessengerNavItem from '../items/MessengerNavItem';
import NotificationsNavItem from '../items/NotificationsNavItem';
import UpgradeButtonNavItem from '../items/UpgradeButtonNavItem';
import UserWebTimeclockNavItem from '../items/UserWebTimeclockNavItem';

import MobileHeaderNav from './MobileHeaderNav';
import PayrollCountdownBanner from './PayrollCountdownBanner';
import PayrollFailedPaymentBanner from './PayrollFailedPaymentBanner';
import PredictedFundingFailureBanner from './PredictedFundingFailureBanner';

const { cx } = cxHelpers('NavView');

import { useIsLargeScreen } from 'fe-design-base/utils/useMediaQuery';
import { useIsEmbedded } from 'hooks/useIsEmbedded';

import PayrollAutopayrollExceededCreditLimitBanner from 'features/nav/PayrollAutopayrollExceededCreditLimitBanner';

export const HeaderNav = ({
  navType,
  isGodModing,
  currentUser,
  mobile,
  collapsedState,
  setCollapsedState,
  showPayrollCountdownBanner,
  whatsNewEnabled,
  onMount,
  shouldShowSimplifiedLeftNav,
}) => {
  // IMPORTANT: Use window.location.pathname instead of withCurrentRoute HOC to avoid
  // re-renders caused by shared browserHistory between multiple Routers
  const currentRoute = window.location.pathname;

  const isEmbedded = useIsEmbedded();
  const isLargeScreen = useIsLargeScreen();
  const isBrowserOnMobile = useIsBrowserOnMobile();
  const dispatch = useDispatch();

  useEffect(() => {
    const queryParams = queryString.parse(window.location.search);
    const drawerParam = queryParams.drawer;

    if (drawerParam === 'notification_drawer') {
      dispatch(toggleNotificationsShown(true));

      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('drawer');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [dispatch]);

  useEffect(() => {
    onMount(['fe_whats_new']);
  }, [onMount]);

  useEffect(() => {
    if (window.Homebase.env !== 'production') {
      datadogLogInfo({
        message: `isBrowserOnMobile`,
        context: {
          currentUser: currentUser.get('id'),
          mobile,
          currentRoute,
          isGodModing,
          navType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickLogo = useCallback(
    () => window.Homebase.RailsReactBridge.navigateToReactView('/home'),
    []
  );

  const handleCollapseLeftNav = useCallback(
    () => setCollapsedState(true),
    [setCollapsedState]
  );

  if (navType === 'hidden') {
    return <GlobalComponents />;
  }

  // For when they are running payroll
  if (FULL_SCREEN_PATHS.some(path => currentRoute.includes(path))) {
    return null;
  }

  // If we are embedded, we don't want to show the header
  if (isEmbedded) {
    return <GlobalComponents />;
  }

  if (isBrowserOnMobile) {
    return <GlobalComponents />;
  }

  // Don't show header nav if left nav is enabled
  if (shouldShowSimplifiedLeftNav) {
    return <GlobalComponents />;
  }

  return (
    <>
      <BillingStateBanner />
      <EnforcementBanner />
      {showPayrollCountdownBanner && <PayrollCountdownBanner />}
      <PayrollFailedPaymentBanner />
      <PayrollAutopayrollExceededCreditLimitBanner />
      <PayrollPreNoteValidationBanner />
      <PredictedFundingFailureBanner />
      {mobile ? (
        <MobileHeaderNav
          navType={navType}
          isGodModing={isGodModing}
          currentUser={currentUser}
          collapsedState={collapsedState}
          setCollapsedState={setCollapsedState}
        />
      ) : (
        <Box>
          <Box
            className={cx({}, 'js-nav')}
            minh={dimensions.navHeight}
            z={11}
            pl={32}
            vcenter
            bg={isGodModing ? 'black' : 'purpleXdark'}
            row
            spacebetween
          >
            <Box onClick={handleClickLogo} pointer>
              <Logo color="mono0" height={16} />
            </Box>

            <Box row vcenter>
              {whatsNewEnabled && isLargeScreen && (
                <Box mr={16}>
                  <AnnounceKitButton />
                </Box>
              )}

              <Box>
                <ActiveTrialNavItem fullText />
              </Box>
              <Box ph={8}>
                <UpgradeButtonNavItem
                  onCollapseLeftNav={handleCollapseLeftNav}
                  newHeader
                  fullText
                />
              </Box>
              <Box ph={8}>
                <UserWebTimeclockNavItem newHeader />
              </Box>
              <Box ph={8}>
                <MessengerNavItem newHeader />
              </Box>
              <Box ph={8}>
                <NotificationsNavItem newHeader />
              </Box>
              <Box ph={8}>
                <LocationsMenuNavItem newHeader mlxs />
              </Box>
              <Box ph={8}>
                <AvatarMenuNavItem newHeader />
              </Box>
            </Box>
          </Box>

          <GlobalComponents />
        </Box>
      )}
    </>
  );
};

HeaderNav.propTypes = {
  showPayrollCountdownBanner: PropTypes.bool,
  shouldShowSimplifiedLeftNav: PropTypes.bool,
};

export default connect(
  (state, props) => ({
    navType: sessionSelectors.getNavType(state, props),
    isGodModing: sessionSelectors.getIsGodModing(state, props),
    currentUser: sessionSelectors.getCurrentUser(state, props),
    showPayrollCountdownBanner:
      sessionSelectors.getShowPayrollCountdownBanner(state),
    collapsedState: state.getIn(['nav', 'leftNavCollapsed']),
    whatsNewEnabled: getViewLogic(state, 'fe_whats_new') === 1,
    shouldShowSimplifiedLeftNav:
      sessionSelectors.getShouldShowSimplifiedLeftNav(state),
  }),
  {
    setCollapsedState: navActions.toggleLeftNav,
    onMount: enrollEntityInExperiment,
  }
)(
  withUxRoot({
    productArea: PRODUCT_AREAS.NAVIGATION,
    eventCategory: EVENT_CATEGORIES.NAV,
  })(HeaderNav)
);
